import React from 'react'

/**
 * Top-level error boundary. Catches any thrown error during render or in a
 * lifecycle method anywhere below it, and shows a recoverable fallback screen
 * instead of leaving the user with a black page.
 *
 * The error is also logged to console with a clear tag so we can diagnose
 * black-screen reports remotely.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error)
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] component stack:', errorInfo?.componentStack)
    this.setState({ errorInfo })
  }

  handleReload = () => {
    try { window.location.reload() } catch {}
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const errMsg = this.state.error?.message || String(this.state.error || 'Unknown error')
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#111',
          color: '#fff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textAlign: 'center',
        }}
      >
        <h1 style={{
          fontSize: '2.4rem',
          margin: 0,
          letterSpacing: '0.04em',
          color: '#C9A84C',
          fontWeight: 800,
        }}>
          Action!
        </h1>
        <p style={{ marginTop: 24, fontSize: '1.1rem', maxWidth: 420 }}>
          Something went wrong.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            marginTop: 20,
            padding: '12px 28px',
            fontSize: '1rem',
            background: '#C9A84C',
            color: '#111',
            border: 'none',
            borderRadius: 8,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Tap to reload
        </button>
        {/* Dev-only error detail — helps diagnose the black screen */}
        {import.meta.env.DEV && (
          <details style={{ marginTop: 32, maxWidth: 600, textAlign: 'left', width: '100%' }}>
            <summary style={{ cursor: 'pointer', color: '#888' }}>Error details (dev)</summary>
            <pre style={{
              marginTop: 8,
              padding: 12,
              background: '#000',
              color: '#e05c5c',
              fontSize: 12,
              overflow: 'auto',
              borderRadius: 4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {errMsg}
              {this.state.errorInfo?.componentStack || ''}
            </pre>
          </details>
        )}
      </div>
    )
  }
}
